import React, { useEffect, useMemo, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, FileDown, Check, ArrowLeft, Wallet, Sparkles, Copy } from "lucide-react";
import { format, parseISO } from "date-fns";

const CURRENCIES = [
  { value: "CNY", label: "¥ CNY (Chinese yuan)" }, { value: "USD", label: "$ USD" },
  { value: "GBP", label: "£ GBP" }, { value: "EUR", label: "€ EUR" },
  { value: "HKD", label: "HK$ HKD" }, { value: "SGD", label: "S$ SGD" },
  { value: "AUD", label: "A$ AUD" }, { value: "CAD", label: "C$ CAD" }, { value: "JPY", label: "¥ JPY" },
];
const SYMBOLS = { CNY: "¥", USD: "$", GBP: "£", EUR: "€", HKD: "HK$", SGD: "S$", AUD: "A$", CAD: "C$", JPY: "¥" };
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function sym(cur) { return SYMBOLS[cur] || ""; }
function money(amount, cur) {
  return `${sym(cur)}${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function compute(form) {
  const base = (form.lines || []).reduce((acc, l) => acc + (Number(l.hours) || 0) * (Number(l.rate) || 0), 0);
  const annual = Number(form.annual_bonus) || 0;
  const others = (form.other_bonuses || []).reduce((acc, b) => acc + (Number(b.amount) || 0), 0);
  const gross = base + annual + others;
  const tax = form.tax_kind === "percent" ? gross * ((Number(form.tax_value) || 0) / 100) : (Number(form.tax_value) || 0);
  return { base_pay: base, others_total: others, gross, tax_amount: tax, net_total: gross - tax };
}

function PayslipEditor({ payslip, staffList, onSaved, onClose }) {
  const initialFromStaff = (staff) => {
    const rates = staff?.default_wage_rates || [];
    return rates.length > 0
      ? rates.map(r => ({ class_type: r.class_type, hours: 0, rate: r.rate }))
      : [{ class_type: "Group", hours: 0, rate: 0 }];
  };
  const isNew = !payslip;
  const [form, setForm] = useState(() => {
    if (payslip) return { ...payslip };
    return {
      staff_id: "", period_kind: "monthly",
      period_year: new Date().getFullYear(), period_month: new Date().getMonth() + 1,
      period_start: "", period_end: "",
      currency: "CNY", lines: [], tax_kind: "percent", tax_value: 10,
      annual_bonus: 0, other_bonuses: [], notes: "",
    };
  });
  const [busy, setBusy] = useState(false);

  const staff = useMemo(() => staffList.find(s => s.id === form.staff_id), [form.staff_id, staffList]);

  function applyStaffSelection(staffId) {
    const s = staffList.find(x => x.id === staffId);
    setForm(f => ({
      ...f,
      staff_id: staffId,
      lines: s ? initialFromStaff(s) : [],
    }));
  }

  function patch(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function patchLine(i, k, v) { setForm(f => { const lines = [...f.lines]; lines[i] = { ...lines[i], [k]: v }; return { ...f, lines }; }); }
  function addLine() { setForm(f => ({ ...f, lines: [...(f.lines || []), { class_type: "", hours: 0, rate: 0 }] })); }
  function removeLine(i) { setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) })); }
  function patchBonus(i, k, v) { setForm(f => { const bs = [...f.other_bonuses]; bs[i] = { ...bs[i], [k]: v }; return { ...f, other_bonuses: bs }; }); }
  function addBonus() { setForm(f => ({ ...f, other_bonuses: [...(f.other_bonuses || []), { title: "", amount: 0 }] })); }
  function removeBonus(i) { setForm(f => ({ ...f, other_bonuses: f.other_bonuses.filter((_, idx) => idx !== i) })); }

  const totals = compute(form);

  async function save(thenExport = false) {
    if (!form.staff_id) { toast.error("Select a staff member"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.lines = (payload.lines || []).filter(l => l.class_type).map(l => ({
        class_type: l.class_type, hours: Number(l.hours) || 0, rate: Number(l.rate) || 0,
      }));
      payload.other_bonuses = (payload.other_bonuses || []).filter(b => b.title).map(b => ({
        title: b.title, amount: Number(b.amount) || 0,
      }));
      payload.tax_value = Number(payload.tax_value) || 0;
      payload.annual_bonus = Number(payload.annual_bonus) || 0;
      let saved;
      if (isNew) {
        const { data } = await api.post("/admin/payslips", payload);
        saved = data;
      } else {
        const { data } = await api.patch(`/admin/payslips/${form.id}`, payload);
        saved = data;
      }
      toast.success(isNew ? "Payslip created" : "Payslip updated");
      if (thenExport) {
        const res = await api.get(`/admin/payslips/${saved.id}/export.pdf`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Dawnville-Payslip-${(saved.staff_name_en || "Staff").replace(/\s+/g, "-")}-${(saved.period_label || "Period").replace(/\s+/g, "-")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("PDF downloaded");
      }
      onSaved?.(saved);
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="payslip-editor" className="bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="payslip-editor-back"><ArrowLeft className="w-4 h-4 mr-1.5" />Back to list</Button>
        <div className="flex items-center gap-2">
          <Button onClick={()=>save(false)} disabled={busy} variant="outline" data-testid="payslip-editor-save" className="rounded-full"><Check className="w-4 h-4 mr-1.5" />Save draft</Button>
          <Button onClick={()=>save(true)} disabled={busy} data-testid="payslip-editor-save-export" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"><FileDown className="w-4 h-4 mr-1.5" />Save &amp; export PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5"><Label className="text-xs">Staff member</Label>
          <Select value={form.staff_id} onValueChange={applyStaffSelection} disabled={!isNew}>
            <SelectTrigger data-testid="payslip-staff" className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{staffList.map(s=><SelectItem key={s.id} value={s.id}>{s.name_en} {s.name_zh ? `(${s.name_zh})` : ""} · {s.staff_type.replace("_"," ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3"><Label className="text-xs">Currency</Label>
          <Select value={form.currency} onValueChange={(v)=>patch("currency", v)}>
            <SelectTrigger data-testid="payslip-currency" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="md:col-span-4"><Label className="text-xs">Period</Label>
          <Select value={form.period_kind} onValueChange={(v)=>patch("period_kind", v)}>
            <SelectTrigger data-testid="payslip-period-kind" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="range">Custom range</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.period_kind === "monthly" ? (
          <>
            <div className="md:col-span-3"><Label className="text-xs">Month</Label>
              <Select value={String(form.period_month)} onValueChange={(v)=>patch("period_month", parseInt(v,10))}>
                <SelectTrigger data-testid="payslip-month" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i)=><SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3"><Label className="text-xs">Year</Label>
              <Input type="number" value={form.period_year || ""} onChange={(e)=>patch("period_year", parseInt(e.target.value, 10))} data-testid="payslip-year" className="mt-1" />
            </div>
          </>
        ) : (
          <>
            <div className="md:col-span-3"><Label className="text-xs">Start</Label>
              <Input type="date" value={form.period_start || ""} onChange={(e)=>patch("period_start", e.target.value)} data-testid="payslip-start" className="mt-1" />
            </div>
            <div className="md:col-span-3"><Label className="text-xs">End</Label>
              <Input type="date" value={form.period_end || ""} onChange={(e)=>patch("period_end", e.target.value)} data-testid="payslip-end" className="mt-1" />
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" />Teaching earnings</p>
          <Button size="sm" variant="outline" onClick={addLine} data-testid="payslip-add-line" className="rounded-full h-7 text-xs"><Plus className="w-3 h-3 mr-1" />Add class type</Button>
        </div>
        {staff && (staff.default_wage_rates?.length || 0) > 0 && (
          <p className="text-xs italic text-[hsl(var(--muted-foreground))] mb-2">Pre-filled from {staff.name_en}&apos;s saved wage rates — you can edit any value below.</p>
        )}
        <div className="space-y-2">
          {(form.lines || []).map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`payslip-line-${i}`}>
              <Input value={l.class_type} onChange={(e)=>patchLine(i, "class_type", e.target.value)} placeholder="e.g. Group" className="col-span-5 h-9 text-sm bg-white" data-testid={`payslip-line-${i}-type`} />
              <div className="col-span-2"><Input type="number" step="0.5" value={l.hours} onChange={(e)=>patchLine(i, "hours", e.target.value)} placeholder="Hours" className="h-9 text-sm bg-white text-right" data-testid={`payslip-line-${i}-hours`} /></div>
              <div className="col-span-2 relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[hsl(var(--muted-foreground))]">{sym(form.currency)}</span>
                <Input type="number" value={l.rate} onChange={(e)=>patchLine(i, "rate", e.target.value)} placeholder="Rate" className="h-9 text-sm bg-white pl-6 text-right" data-testid={`payslip-line-${i}-rate`} />
              </div>
              <div className="col-span-2 text-right text-sm font-display tabular-nums">{money((Number(l.hours)||0) * (Number(l.rate)||0), form.currency)}</div>
              <div className="col-span-1 flex justify-end"><Button size="sm" variant="ghost" onClick={()=>removeLine(i)} className="h-7 w-7 p-0 text-[hsl(var(--destructive))]" data-testid={`payslip-line-${i}-remove`}><X className="w-3.5 h-3.5" /></Button></div>
            </div>
          ))}
          {(form.lines || []).length === 0 && <p className="text-xs italic text-[hsl(var(--muted-foreground))] py-3 text-center">No classes yet — add the first row.</p>}
        </div>
        <div className="mt-3 flex justify-end text-sm">
          <span className="text-[hsl(var(--muted-foreground))] mr-2">Base pay subtotal:</span>
          <span className="font-display tabular-nums" data-testid="payslip-base-pay">{money(totals.base_pay, form.currency)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Bonuses</p>
          <div>
            <Label className="text-xs">Annual bonus</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[hsl(var(--muted-foreground))]">{sym(form.currency)}</span>
              <Input type="number" value={form.annual_bonus} onChange={(e)=>patch("annual_bonus", e.target.value)} className="pl-7 text-right bg-white" data-testid="payslip-annual-bonus" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-xs">Other bonuses (titled)</Label>
              <Button size="sm" variant="outline" onClick={addBonus} className="h-7 text-xs rounded-full" data-testid="payslip-add-bonus"><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            <div className="mt-2 space-y-2">
              {(form.other_bonuses || []).map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`payslip-bonus-${i}`}>
                  <Input value={b.title} onChange={(e)=>patchBonus(i, "title", e.target.value)} placeholder="Bonus title (e.g. Performance)" className="col-span-7 h-9 text-sm bg-white" data-testid={`payslip-bonus-${i}-title`} />
                  <div className="col-span-4 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[hsl(var(--muted-foreground))]">{sym(form.currency)}</span>
                    <Input type="number" value={b.amount} onChange={(e)=>patchBonus(i, "amount", e.target.value)} className="h-9 text-sm pl-6 bg-white text-right" data-testid={`payslip-bonus-${i}-amount`} />
                  </div>
                  <Button size="sm" variant="ghost" onClick={()=>removeBonus(i)} className="col-span-1 h-7 w-7 p-0 text-[hsl(var(--destructive))]" data-testid={`payslip-bonus-${i}-remove`}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              {(form.other_bonuses || []).length === 0 && <p className="text-xs italic text-[hsl(var(--muted-foreground))]">No extra bonuses.</p>}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">Tax &amp; deductions</p>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5"><Label className="text-xs">Tax type</Label>
              <Select value={form.tax_kind} onValueChange={(v)=>patch("tax_kind", v)}>
                <SelectTrigger data-testid="payslip-tax-kind" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">% of gross</SelectItem>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-7"><Label className="text-xs">{form.tax_kind === "percent" ? "Percentage (%)" : "Amount"}</Label>
              <div className="relative mt-1">
                {form.tax_kind === "fixed" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[hsl(var(--muted-foreground))]">{sym(form.currency)}</span>}
                <Input type="number" step={form.tax_kind === "percent" ? "0.1" : "1"} value={form.tax_value} onChange={(e)=>patch("tax_value", e.target.value)} className={`bg-white text-right ${form.tax_kind === "fixed" ? "pl-7" : ""}`} data-testid="payslip-tax-value" />
                {form.tax_kind === "percent" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[hsl(var(--muted-foreground))]">%</span>}
              </div>
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">Tax to deduct:</span>
            <span className="font-display tabular-nums" data-testid="payslip-tax-amount">{money(totals.tax_amount, form.currency)}</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Base pay</p><p className="font-display text-xl tabular-nums mt-1">{money(totals.base_pay, form.currency)}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Bonuses</p><p className="font-display text-xl tabular-nums mt-1">{money(totals.others_total + (Number(form.annual_bonus)||0), form.currency)}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Tax</p><p className="font-display text-xl tabular-nums mt-1 text-[hsl(var(--destructive))]">− {money(totals.tax_amount, form.currency)}</p></div>
          <div className="border-l border-[hsl(var(--primary))]/20 pl-4"><p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">Net payable</p><p className="font-display text-2xl tabular-nums mt-1 text-[hsl(var(--primary))]" data-testid="payslip-net-total">{money(totals.net_total, form.currency)}</p></div>
        </div>
      </div>

      <div><Label className="text-xs">Notes (printed on PDF)</Label>
        <Textarea rows={2} value={form.notes || ""} onChange={(e)=>patch("notes", e.target.value)} data-testid="payslip-notes" className="mt-1" />
      </div>
    </div>
  );
}

export default function PayslipsPanel() {
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [view, setView] = useState({ mode: "list", payslip: null });

  async function load() {
    try {
      const [ps, st] = await Promise.all([api.get("/admin/payslips"), api.get("/admin/staff")]);
      setItems(ps.data); setStaffList(st.data);
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function exportPdf(p) {
    try {
      const res = await api.get(`/admin/payslips/${p.id}/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Dawnville-Payslip-${(p.staff_name_en || "Staff").replace(/\s+/g, "-")}-${(p.period_label || "Period").replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function duplicate(p) {
    try {
      const { data } = await api.post(`/admin/payslips/${p.id}/duplicate`);
      toast.success(`Duplicated · now editing ${data.period_label}`);
      setView({ mode: "edit", payslip: data });
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function del(p) {
    if (!window.confirm(`Delete payslip for ${p.staff_name_en} · ${p.period_label}?`)) return;
    try { await api.delete(`/admin/payslips/${p.id}`); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  if (view.mode === "edit") {
    return (
      <PayslipEditor
        payslip={view.payslip}
        staffList={staffList}
        onClose={()=>{ setView({ mode: "list", payslip: null }); load(); }}
        onSaved={(saved)=>{ setView({ mode: "edit", payslip: saved }); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-payslips-panel" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Payslips · {items.length}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Create editable payslips per staff and per period. Pull wage rates from each staff profile, add bonuses, deduct tax, then export to PDF.</p>
        </div>
        <Button size="sm" onClick={()=>setView({ mode: "edit", payslip: null })} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="payslip-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" />New payslip
        </Button>
      </div>
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              <th className="py-2 px-3">Staff</th><th className="py-2 px-3">Period</th>
              <th className="py-2 px-3">Currency</th><th className="py-2 px-3 text-right">Base</th>
              <th className="py-2 px-3 text-right">Bonus</th><th className="py-2 px-3 text-right">Tax</th>
              <th className="py-2 px-3 text-right">Net</th><th className="py-2 px-3">Updated</th>
              <th className="py-2 px-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} data-testid={`payslip-row-${p.id}`} className="border-t border-[hsl(var(--border))]/60 hover:bg-[hsl(var(--muted))]/30">
                <td className="py-2 px-3">{p.staff_name_en}<div className="text-xs text-[hsl(var(--muted-foreground))]">{p.staff_name_zh || ""}</div></td>
                <td className="py-2 px-3 text-xs">{p.period_label || "—"}</td>
                <td className="py-2 px-3 text-xs">{p.currency}</td>
                <td className="py-2 px-3 text-xs text-right tabular-nums">{money(p.base_pay, p.currency)}</td>
                <td className="py-2 px-3 text-xs text-right tabular-nums">{money((Number(p.annual_bonus)||0) + (Number(p.other_bonuses_total)||0), p.currency)}</td>
                <td className="py-2 px-3 text-xs text-right tabular-nums text-[hsl(var(--destructive))]">−{money(p.tax_amount, p.currency)}</td>
                <td className="py-2 px-3 text-sm font-display tabular-nums text-right text-[hsl(var(--primary))]">{money(p.net_total, p.currency)}</td>
                <td className="py-2 px-3 text-xs">{p.updated_at ? format(parseISO(p.updated_at), "MMM d, HH:mm") : ""}</td>
                <td className="py-2 px-3 flex gap-1">
                  <Button size="sm" variant="ghost" onClick={()=>setView({ mode: "edit", payslip: p })} data-testid={`payslip-edit-${p.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>duplicate(p)} data-testid={`payslip-dup-${p.id}`} title="Duplicate to next month"><Copy className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>exportPdf(p)} data-testid={`payslip-pdf-${p.id}`} title="Export PDF"><FileDown className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(p)} data-testid={`payslip-del-${p.id}`} className="text-[hsl(var(--destructive))]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-[hsl(var(--muted-foreground))] text-xs italic">No payslips yet — click &ldquo;New payslip&rdquo; to create the first one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
